import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CheckboxStandalone,
  Column,
  Columns,
  Heading,
  Inline,
  List,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Routes from 'src/config/routes';
import {
  ListingProductCode,
  PurchaseFreemiumDocument,
  type ListingProduct,
} from 'src/generated/graphql';
import { fireListingCoreProductPurchaseEvent } from 'src/utils/analytics';
import { clearSelectedProduct } from 'src/utils/cookies';

interface FreeListingContractProps {
  listingId: string | null;
  freemiumProduct: ListingProduct;
}

export const FreeListingContract = (props: FreeListingContractProps) => {
  const [checked, setChecked] = useState(false);
  const [touched, setTouched] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const displayErrorMessage = touched && !checked;

  const [purchaseFreemium, { loading, error }] = useMutation(
    PurchaseFreemiumDocument,
  );

  const onSubmit = async () => {
    setTouched(true);

    if (!checked) {
      return;
    }

    try {
      await purchaseFreemium();
    } catch {
      // Do nothing, we'll handle via the mutation's error
      return;
    }

    clearSelectedProduct();
    // This is so that we can track conversions of Freemium clients the same way as others
    fireListingCoreProductPurchaseEvent(ListingProductCode.Freemium);
    dispatch({ type: 'REQUEST_PAYMENT_SUCCESS' });

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('productToken', props.freemiumProduct.productToken);
    if (props.listingId) {
      newSearchParams.set('listingId', props.listingId);
    }

    navigate(`${Routes.PAYMENT_SUCCESS}?${newSearchParams.toString()}`);
  };

  if (loading) {
    return (
      <Box data={{ testid: 'freemium-contract-mutation-loader' }}>
        <CustomLoader />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert tone="critical" data={{ testid: 'freemium-contract-error' }}>
        <Text>
          An error occurred, please{' '}
          <TextLink
            href="https://www.seekbusiness.com.au/contact-us"
            hitArea="large"
          >
            contact us
          </TextLink>
        </Text>
      </Alert>
    );
  }

  return (
    <Box data={{ testid: 'freemium-product-details' }}>
      <Stack space="large">
        <Stack space="medium">
          <Heading level="2" component="h1">
            Review and accept Free listing terms
          </Heading>

          <Text>
            Before creating your Free business listing, please review and accept
            these terms:
          </Text>
        </Stack>

        <Box paddingLeft="medium">
          <List>
            <Text>
              “Free” means you won&apos;t be charged for posting your ad on the
              SEEK Business website.
            </Text>
            <Text>
              You can only have one Free listing on SEEK Business at a time.
            </Text>
            <Text>
              Your Free listing will stay on the site for up to 6 months, but
              you can remove it any time.
            </Text>
            <Text>
              Free listings appear lower in search results than paid listings
              and have a different appearance.
            </Text>
            <Text>
              If you want to upgrade to a paid listing later, you&apos;ll need
              to create a new listing.
            </Text>
            <Text>
              Free listings are only available for individuals selling a
              business they own, not for third parties assisting with the sale.
            </Text>
          </List>
        </Box>

        <Text data={{ testid: 'freemium-contract-contact-us' }}>
          For any further questions, please email us at{' '}
          <TextLink weight="weak" href="mailto:enquiries@seekbusiness.com.au">
            enquiries@seekbusiness.com.au
          </TextLink>
          .
        </Text>

        <Columns space="xsmall">
          <Column width="content">
            <CheckboxStandalone
              id={contractTermsCheckboxId}
              aria-labelledby={contractTermsCheckboxLabelId}
              data={{ testid: 'freemium-contract-checkbox' }}
              checked={checked}
              onChange={() => {
                setTouched(true);
                setChecked((prevState) => !prevState);
              }}
              tone={displayErrorMessage ? 'critical' : 'neutral'}
            />
          </Column>

          <Column>
            <Stack space="small">
              <Box
                component="label"
                cursor="pointer"
                htmlFor={contractTermsCheckboxId}
                id={contractTermsCheckboxLabelId}
                userSelect="none"
              >
                <Stack space="medium">
                  <Text>
                    You warrant that you have the legal authority to enter into
                    this agreement on behalf of your organisation and
                    acknowledge and agree that you have read and understood all
                    the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/terms-and-conditions"
                      target="_blank"
                    >
                      Terms and Conditions
                    </TextLink>{' '}
                    of this agreement (including the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/contract-terms"
                      target="_blank"
                    >
                      Advertising Terms
                    </TextLink>
                    ) and are aware that the terms contain limitations on SEEK’s
                    liability.
                  </Text>
                </Stack>
              </Box>

              {displayErrorMessage && (
                <Text tone="critical" size="small">
                  <i>
                    You must tick the checkbox in order to accept these terms.
                  </i>
                </Text>
              )}
            </Stack>
          </Column>
        </Columns>

        <Stack space="medium">
          <Text>
            By continuing, you are entering into an agreement with SEEK and will
            be bound by the terms and conditions contained herein.
          </Text>

          <Inline space="small">
            <Button
              // Realistically we shouldn't see this button when loading due to
              // the above `if (loading)` check but I'm putting this here just in case
              loading={loading}
              type="submit"
              tone="formAccent"
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              data={{
                htag: 'freemium-accept-cta',
                analytics: 'freemium-accept-cta',
              }}
            >
              Accept terms and continue
            </Button>
          </Inline>
        </Stack>
      </Stack>
    </Box>
  );
};

const contractTermsCheckboxId = 'contract-checkbox';
const contractTermsCheckboxLabelId = 'contract-checkbox-label';
